import { scrollToId } from "@/lib/scroll";
import { Reveal, SectionHead } from "./Reveal";

const tiers = [
  {
    label: "Private Care",
    category: "Monthly Maintenance",
    founding: "$149",
    standard: "$169",
    description: "Consistent monthly care for vehicles already maintained to DARK standard.",
    features: [
      "One scheduled monthly visit",
      "Interior maintenance",
      "Exterior maintenance",
      "Scheduling priority",
      "Preferred member rates on select add-ons",
    ],
    cta: "Apply for Private Care",
    interest: "PRIVATE CARE",
    badge: null,
    testid: "membership-private-care",
  },
  {
    label: "Black Access",
    category: "Priority Membership",
    price: "From $289",
    description: "A more curated level of care for clients who want stronger scheduling priority, consistency, and a more personalized service relationship.",
    features: [
      "Priority scheduling",
      "Personalized vehicle notes",
      "Concierge-style communication",
      "Preferred rates on select treatments",
      "Limited client capacity",
    ],
    cta: "Request Black Access",
    interest: "BLACK ACCESS",
    badge: "Limited Capacity",
    testid: "membership-black-access",
  },
];

export const Membership = ({ onApply }) => (
  <section id="membership" data-testid="membership-section" className="py-28 sm:py-40 px-5 sm:px-8">
    <div className="max-w-7xl mx-auto">
      <SectionHead
        index="02"
        overline="Membership"
        title="Standards are easier to maintain than rebuild."
        copy="Membership is designed for vehicles already maintained to DARK standard."
      />
      <div className="grid md:grid-cols-2 gap-x-16 gap-y-16 mt-20 max-w-5xl">
        {tiers.map((t, i) => (
          <Reveal key={t.label} delay={i * 0.12}>
            <article data-testid={t.testid} className="border-t border-graphite pt-10 flex flex-col h-full">
              <div className="flex items-center gap-4">
                <p className="font-accent text-[10px] tracking-[0.35em] uppercase text-silver/55">{t.category}</p>
                {t.badge && (
                  <span data-testid="black-access-badge" className="font-accent text-[9px] tracking-[0.3em] uppercase text-volt border border-volt/40 px-2.5 py-1">
                    {t.badge}
                  </span>
                )}
              </div>
              <h3 className="font-display text-3xl sm:text-4xl text-ivory mt-5 tracking-[0.04em] uppercase">{t.label}</h3>
              <div className="mt-6">
                {t.founding ? (
                  <div className="flex items-baseline gap-5 flex-wrap">
                    <span className="font-display text-4xl metallic-text" data-testid="private-care-founding-rate">
                      {t.founding}<span className="text-lg text-silver/60"> / month</span>
                    </span>
                    <span className="font-accent text-[10px] tracking-[0.25em] uppercase text-volt">Founding rate</span>
                    <span className="text-silver/50 text-sm" data-testid="private-care-standard-rate">
                      Standard {t.standard} / month
                    </span>
                  </div>
                ) : (
                  <span className="font-display text-4xl metallic-text" data-testid="black-access-price">
                    {t.price}<span className="text-lg text-silver/60"> / month</span>
                  </span>
                )}
              </div>
              <p className="text-silver/70 text-sm leading-relaxed mt-6">{t.description}</p>
              <ul className="mt-7 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="text-sm text-silver/80 py-2.5 border-t border-graphite/60 first:border-t-0">
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`${t.testid}-cta`}
                onClick={() => { onApply?.(t.interest); scrollToId("request"); }}
                className={`mt-8 self-start font-accent text-xs tracking-[0.25em] uppercase px-8 py-4 transition-colors duration-300 ${
                  t.badge
                    ? "cta-shadow bg-volt text-white hover:bg-[#4A8FFF]"
                    : "border border-silver/25 text-silver hover:border-volt hover:text-ivory"
                }`}
              >
                {t.cta}
              </button>
            </article>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.25}>
        <p className="mt-16 text-silver/50 text-xs max-w-lg leading-relaxed" data-testid="membership-capacity-note">
          Membership capacity is intentionally limited to protect service quality and scheduling consistency.
        </p>
      </Reveal>
    </div>
  </section>
);
